import { EditorView } from "@codemirror/view";

// Markdown list / quote line shapes. The optional `[ ] ` group captures GFM
// task-list checkboxes so continuation can reset them to unchecked.
const UNORDERED = /^(\s*)([-*+])(\s+)(\[[ xX]\]\s)?(.*)$/;
const ORDERED = /^(\s*)(\d+)([.)])(\s+)(\[[ xX]\]\s)?(.*)$/;
const BLOCKQUOTE = /^(\s*>+\s?)(.*)$/;

/**
 * Smart Enter for Markdown: continue the current bullet / ordered / task-list
 * item or blockquote on the next line, incrementing ordered numbers and
 * resetting task checkboxes. Pressing Enter on an *empty* item removes the
 * marker instead (exits the list). Falls back to the default newline when the
 * cursor isn't at the end of a list/quote line.
 */
export const continueListOnEnter = (view: EditorView): boolean => {
  const { state } = view;
  const range = state.selection.main;
  // Only handle a single collapsed cursor sitting at the end of its line.
  if (!range.empty) return false;
  const line = state.doc.lineAt(range.head);
  if (range.head !== line.to) return false;
  const text = line.text;

  const unordered = UNORDERED.exec(text);
  if (unordered) {
    const [, indent, bullet, spaces, checkbox, content] = unordered;
    if (content.trim() === "") return clearMarker(view, line.from, range.head);
    return insertContinuation(view, range.head, `${indent}${bullet}${spaces}${checkbox ? "[ ] " : ""}`);
  }

  const ordered = ORDERED.exec(text);
  if (ordered) {
    const [, indent, num, delim, spaces, checkbox, content] = ordered;
    if (content.trim() === "") return clearMarker(view, line.from, range.head);
    const next = String(parseInt(num, 10) + 1);
    return insertContinuation(view, range.head, `${indent}${next}${delim}${spaces}${checkbox ? "[ ] " : ""}`);
  }

  const quote = BLOCKQUOTE.exec(text);
  if (quote) {
    const [, prefix, content] = quote;
    if (content.trim() === "") return clearMarker(view, line.from, range.head);
    return insertContinuation(view, range.head, prefix);
  }

  return false;
};

const insertContinuation = (view: EditorView, at: number, marker: string): boolean => {
  const insert = `\n${marker}`;
  view.dispatch({
    changes: { from: at, insert },
    selection: { anchor: at + insert.length },
    scrollIntoView: true,
    userEvent: "input",
  });
  return true;
};

// Replace the marker (line start → cursor) with nothing, leaving an empty line.
const clearMarker = (view: EditorView, from: number, to: number): boolean => {
  view.dispatch({
    changes: { from, to, insert: "" },
    selection: { anchor: from },
    userEvent: "delete",
  });
  return true;
};
